import React, { FC, useCallback, useState, useEffect, useContext, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import get from '../../../utils/accessors/get';

import getScoped from '../../../utils/redux/getScoped';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import useScopedSelector from '../../../hooks/useScopedSelector';
import { FormComposedProps } from './Form';
import FormContext from '../FormContext';

const withInitialValues = <Values, >(WrappedComponent: FC<FormComposedProps<Values>>) => {
  const WithInitialValues = (props: FormComposedProps<Values>) => {
    const { name, initialValues, valuesSrc, store, scope, defaultValues } = props;

    const version = useScopedSelector(`${name}.version`) || 0;

    const {
      initialValues: parentInitialValues, parentDefaultValues,
    } = useContext(FormContext);

    const getInitialValues = useCallback(() => {
      if (initialValues) {
        return initialValues;
      }

      if (valuesSrc) {
        const state = store.getState();
        return getScoped(state, valuesSrc, scope);
      }

      return undefined;
    }, [initialValues, scope, store, valuesSrc]);

    const [cachedInitialValues, setCachedInitialValues] = useState(getInitialValues);

    const latestInitialValues = useMemo(() => getInitialValues(), [getInitialValues]);

    useEffect(() => {
      if (version !== 0) {
        setCachedInitialValues(getInitialValues);
      }
    }, [getInitialValues, version]);

    return (
      <WrappedComponent
        {...props}
        latestInitialValues={latestInitialValues}
        initialValues={cachedInitialValues || get(parentInitialValues, name)}
        defaultValues={defaultValues || get(parentDefaultValues, name)}
      />
    );
  };

  WithInitialValues.displayName = getWrappedDisplayName('WithInitialValues', WrappedComponent);

  return hoistNonReactStatics(WithInitialValues, WrappedComponent);
};

export default withInitialValues;
